// ══════════════════════════════════════════════════════════════════════════════
//  src/components/teacher/ClasesEmpresa.jsx
//  LAS CLASES DE EMPRESA DEL PROFESOR — pestaña «Empresas» de su portal
//
//  Rob (EMPRESAS.md, D3): «no tenemos un lugar donde el teacher pueda ingresar
//  y evaluar a su alumno […] asistencia, retardo o no llegó —faltó—, registrar
//  la fecha y hora de la clase».
//
//  Aquí NO se programa ni se cancela nada: eso lo hace gerencia desde el CRM,
//  como el resto de cambios de clase. El profesor ve sus clases de empresa y,
//  cuando ya empezaron, pasa lista y evalúa.
//
//  Lo que tiene sin reportar sale ARRIBA y aparte del mes: una clase de la
//  semana pasada sin reporte es lo único de esta pantalla que corre prisa.
// ══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useClasesEmpresa } from './useProfesor.js';
import ModalReporteEmpresa from './ModalReporteEmpresa.jsx';

const FORMATO = { individual: 'Individual', grupal: 'Grupal', business: 'Business' };

// [texto, clase de color]
const REPORTE = {
  futura:    ['Programada', ''],
  pendiente: ['Sin reporte', 'pp-er'],
  entregado: ['Reportada', 'pp-ok'],
  cerrado:   ['Cerrada', 'pp-ok'],
  cancelada: ['Cancelada', 'pp-er'],
};

const CANCELADA_POR = { empleado: 'el empleado', empresa: 'la empresa', profesor: 'el profesor', nsl: 'NSL' };

/** «2026-09-14» → «lun 14 sep». Al mediodía, para que ningún huso lo mueva de día. */
function diaCorto(iso) {
  return new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

function Fila({ c, onAbrir }) {
  const [txt, color] = REPORTE[c.estado_reporte] || ['—', ''];
  const quien = c.grupo ? `${c.grupo.nombre} · ${c.grupo.nivel}` : FORMATO[c.formato];
  return (
    <div className="pp-clase" data-clase-empresa={c.id}>
      <span className="pp-clase-fecha">{diaCorto(c.fecha)}</span>
      <span className="pp-clase-hora" style={{ minWidth: 52 }}>{c.hora}</span>
      <span className="pp-clase-alumno">
        {c.empresa}
        <span className="pp-card-s" style={{ display: 'block' }}>
          {quien} · {c.participantes} {c.participantes === 1 ? 'persona' : 'personas'} · {c.duracion_min} min
          {c.estado_reporte === 'cancelada' && c.cancelada_por ? ` · la canceló ${CANCELADA_POR[c.cancelada_por] || c.cancelada_por}` : ''}
        </span>
      </span>
      <span className={`pp-estado ${color}`}>{txt}</span>
      {c.estado_reporte === 'pendiente' && (
        <button className="pp-btn pp-btn-ac" onClick={() => onAbrir(c.id)}>Pasar lista</button>
      )}
      {c.estado_reporte === 'entregado' && (
        <button className="pp-btn" onClick={() => onAbrir(c.id)}>Ver o corregir</button>
      )}
      {c.estado_reporte === 'cerrado' && (
        <button className="pp-btn" onClick={() => onAbrir(c.id)}>Ver reporte</button>
      )}
      {c.estado_reporte === 'futura' && (
        <button className="pp-btn" disabled title="Se pasa lista cuando la clase ya empezó">Pasar lista</button>
      )}
    </div>
  );
}

export default function ClasesEmpresa({ mes, tituloMes, onAnterior, onSiguiente }) {
  const q = useClasesEmpresa(mes);
  const [abierta, setAbierta] = useState(null);
  const [hecho, setHecho] = useState('');

  useEffect(() => {
    if (!hecho) return undefined;
    const t = setTimeout(() => setHecho(''), 5000);
    return () => clearTimeout(t);
  }, [hecho]);

  const d = q.data;
  const pendientes = d?.pendientes || [];

  return (
    <>
      {hecho && <div className="pp-listo" role="status">{hecho}</div>}

      {pendientes.length > 0 && (
        <section className="pp-card pp-card-urgente" data-prueba="pendientes-empresa">
          <div className="pp-card-h">
            <div>
              <div className="pp-card-t">
                {d.total_pendientes === 1 ? 'Tienes 1 clase sin reporte' : `Tienes ${d.total_pendientes} clases sin reporte`}
              </div>
              <div className="pp-card-s">
                Sin tu reporte, la empresa no ve la asistencia ni la evaluación de esa clase.
              </div>
            </div>
          </div>
          <div className="pp-lista">
            {pendientes.map((c) => <Fila key={`p-${c.id}`} c={c} onAbrir={setAbierta} />)}
          </div>
        </section>
      )}

      <section className="pp-card">
        <div className="pp-card-h">
          <div>
            <div className="pp-card-t" style={{ textTransform: 'capitalize' }}>{tituloMes}</div>
            <div className="pp-card-s">
              Las programa gerencia. Aquí pasas lista y evalúas cuando la clase ya empezó;
              puedes corregir el reporte hasta {d?.dias_para_corregir ?? 7} días después.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="pp-btn" onClick={onAnterior}>← Anterior</button>
            <button className="pp-btn" onClick={onSiguiente}>Siguiente →</button>
          </div>
        </div>

        {q.isLoading && <div className="pp-cargando">Cargando clases…</div>}
        {q.isError && <div className="pp-error">{q.error?.message}</div>}
        {!q.isLoading && !q.isError && (d?.clases || []).length === 0 && (
          <div className="pp-vacio">
            <div className="pp-vacio-t">Ninguna clase de empresa este mes</div>
            <div className="pp-vacio-s">Si esperabas alguna, avisa a gerencia.</div>
          </div>
        )}
        {(d?.clases || []).length > 0 && (
          <div className="pp-lista">
            {d.clases.map((c) => <Fila key={c.id} c={c} onAbrir={setAbierta} />)}
          </div>
        )}
      </section>

      {abierta && (
        <ModalReporteEmpresa
          claseId={abierta}
          onCerrar={(entregado) => {
            setAbierta(null);
            if (entregado) setHecho('Reporte entregado. Gracias: la empresa ya puede ver la asistencia.');
          }}
        />
      )}
    </>
  );
}
